<template>
  <div class="entityRow" :class="[`variant-${variant}`]">
    <div v-if="!hideIcon" class="iconBox">
      <AppIcon
        v-if="iconKey"
        :name="iconKey"
        :color="iconColor || 'var(--muted)'"
        :size="18"
      />
      <FavIcon
        v-else-if="domain"
        :domain="domain"
        :size="18"
      />
      <img
        v-else-if="iconType === 'image' && iconSrc && !errored"
        :src="iconSrc"
        :alt="label"
        class="iconImg"
        loading="lazy"
        draggable="false"
        @error="errored = true"
      />
      <HeroIcon
        v-else-if="iconType === 'hero' && iconName"
        :name="iconName"
        :iconStyle="iconStyle || 'outline'"
        :color="iconColor || 'var(--muted)'"
        :size="18"
      />
      <div v-else class="fallback">{{ fallbackLetter }}</div>
    </div>

    <div class="textCol">
      <div class="primaryText">
        <slot name="primary">{{ label }}</slot>
      </div>
      <div v-if="$slots.secondary || secondaryText" class="secondaryText muted">
        <slot name="secondary">{{ secondaryText }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import AppIcon from './AppIcon.vue'
import FavIcon from './FavIcon.vue'
import HeroIcon from './HeroIcon.vue'

const props = defineProps({
  // For account icons
  domain: { type: String, default: '' },
  label: { type: String, default: '' },
  secondaryText: { type: String, default: '' },
  variant: { type: String, default: 'stacked' },
  
  // For custom icons
  iconKey: { type: String, default: '' },
  iconType: { type: String, default: 'image' }, // 'image' | 'hero' | 'fallback'
  iconSrc: { type: String, default: '' },
  iconName: { type: String, default: '' },
  iconStyle: { type: String, default: 'outline' },
  iconColor: { type: String, default: '' },
  hideIcon: { type: Boolean, default: false },
})

const errored = ref(false)

const fallbackLetter = computed(() => {
  const basis = (props.label || props.domain || '').trim()
  if (!basis) return ''
  return basis.slice(0, 1).toUpperCase()
})

const iconSrc = computed(() => props.iconSrc)
</script>

<style scoped>
.entityRow {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.entityRow.variant-stacked {
  align-items: flex-start;
}

.entityRow.variant-stacked .textCol {
  min-height: 34px;
  justify-content: center;
  gap: 2px;
}

.entityRow.variant-single .textCol {
  min-height: 34px;
  justify-content: center;
}

.entityRow.variant-single .primaryText {
  line-height: 34px;
}

.iconBox {
  width: 34px;
  height: 34px;
  border: 1px solid var(--border);
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: transparent;
  flex-shrink: 0;
}

.iconImg {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.fallback {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 11px;
  color: var(--text);
  background: var(--badge-bg);
}

.textCol {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.primaryText {
  font-weight: 700;
  font-size: 15px;
  color: var(--text);
  line-height: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.secondaryText {
  font-size: 13px;
  line-height: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
